/**
 * Global Event Bus for Cross-Component Communication
 * 
 * This event bus allows different views to communicate when important
 * events happen that require cache invalidation or data refresh.
 */

import { ref, type Ref } from 'vue'

// Event types
export type EventType = 
  | 'emails:invalidate'    // Invalidate emails cache
  | 'followups:created'    // Follow-ups were created
  | 'emails:sent'          // Emails were sent
  | 'emails:approved'      // Emails were approved

// Event listener type
type EventListener<T = any> = (data?: T) => void

class EventBus {
  private listeners: Map<EventType, Set<EventListener>> = new Map()
  
  /**
   * Subscribe to an event
   */
  on<T = any>(event: EventType, listener: EventListener<T>) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(listener)
    
    // Return unsubscribe function
    return () => {
      this.listeners.get(event)?.delete(listener)
    }
  }
  
  /**
   * Emit an event to all listeners
   */
  emit<T = any>(event: EventType, data?: T) {
    console.log(`[EventBus] Emitting event: ${event}`, data)
    const listeners = this.listeners.get(event)
    if (listeners && listeners.size > 0) {
      console.log(`[EventBus] Found ${listeners.size} listeners for ${event}`)
      listeners.forEach(listener => {
        try {
          listener(data)
        } catch (error) {
          console.error(`[EventBus] Error in event listener for ${event}:`, error)
        }
      })
    } else {
      console.warn(`[EventBus] No listeners found for event: ${event}`)
    }
  }
  
  /**
   * Remove all listeners for an event
   */
  off(event: EventType) {
    this.listeners.delete(event)
  }
  
  /**
   * Remove all listeners
   */
  clear() {
    this.listeners.clear()
  }
}

// Global event bus instance
export const eventBus = new EventBus()

// Composable for easier usage in components
export function useEventBus() {
  return {
    on: eventBus.on.bind(eventBus),
    emit: eventBus.emit.bind(eventBus),
    off: eventBus.off.bind(eventBus)
  }
}

// Helper functions for common events
export const emailEvents = {
  /**
   * Notify that emails cache should be invalidated
   */
  invalidateCache: () => {
    console.log('[emailEvents] invalidateCache called')
    eventBus.emit('emails:invalidate')
    // Also set localStorage flag as backup
    localStorage.setItem('emails_cache_invalidate', Date.now().toString())
  },
  
  /**
   * Notify that follow-ups were created
   */
  followUpsCreated: (count: number) => {
    console.log(`[emailEvents] followUpsCreated called with count: ${count}`)
    eventBus.emit('followups:created', { count })
    // Also invalidate emails cache since new follow-up emails were created
    eventBus.emit('emails:invalidate')
    // Also set localStorage flag as backup
    localStorage.setItem('emails_cache_invalidate', Date.now().toString())
  },
  
  /**
   * Notify that emails were sent
   */
  emailsSent: (emailIds: string[]) => {
    eventBus.emit('emails:sent', { emailIds })
    eventBus.emit('emails:invalidate')
  },
  
  /**
   * Notify that emails were approved
   */
  emailsApproved: (emailIds: string[]) => {
    eventBus.emit('emails:approved', { emailIds })
    eventBus.emit('emails:invalidate')
  }
}